import React, { FC } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import { ChevronLeft } from "@yleisradio/yds-icons-react";
import styled, { css } from "styled-components";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { UI_SIZE } from "../../enums";
import { localizedText } from "../../helpers/previewHelpers";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { fontFamily } from "../../styles/global";
import { focusColor } from "../../styles/variables";
import { Language } from "../../types/common";

const BackButton = styled.button<{
  uiSize: UI_SIZE;
}>`
  background: transparent;
  border-radius: 0;
  border: none;
  color: ${darkTheme.ACTION_PRIMARY};
  cursor: pointer;
  height: 100%;
  left: 0;
  margin-right: 8px;
  pointer-events: all;
  position: absolute;
  text-align: left;
  top: 0;
  width: 100%;
  z-index: 99;

  ${({ uiSize }) =>
    uiSize === UI_SIZE.LARGE
      ? css`
          padding: 5px 0;
        `
      : css`
          padding: 0;
          top: -3px;
        `}

  &:focus-visible {
    outline: 1px solid ${focusColor} !important;
  }
`;

const TitleNavigation = styled.div<{
  backButton: boolean;
}>`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  padding: ${({ backButton }) =>
    backButton ? "4px 8px 4px 32px" : "4px 8px 4px 0"};
  pointer-events: none;
  position: relative;
`;

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  pointer-events: none;

  span {
    color: ${darkTheme.TEXT_DEFAULT};
    ${fontFamily}
  }
`;

const PrimaryTitle = styled.span<{ isMobile: boolean; uiSize: UI_SIZE }>`
  pointer-events: none;
  ${({ isMobile, uiSize }) =>
    isMobile
      ? css`
          font-size: 14px;
          line-height: 21px;
        `
      : uiSize === UI_SIZE.LARGE
        ? css`
            font-size: 24px;
            line-height: 34px;
          `
        : css`
            font-size: 18px;
            line-height: 27px;
          `}
`;

const SecondaryTitle = styled.span<{ isMobile: boolean; uiSize: UI_SIZE }>`
  pointer-events: none;
  ${({ isMobile, uiSize }) =>
    isMobile
      ? css`
          font-size: 12px;
          line-height: 18px;
        `
      : uiSize === UI_SIZE.LARGE
        ? css`
            font-size: 16px;
            line-height: 24px;
          `
        : css`
            font-size: 14px;
            line-height: 21px;
          `}
`;

function getSecondaryTitle({
  episodeNumber,
  episodeTitle,
  language,
  seasonNumber
}: {
  episodeNumber: number | undefined;
  episodeTitle: string | undefined;
  language: Language;
  seasonNumber: number | undefined;
}): string | undefined {
  if (!episodeNumber) {
    return episodeTitle;
  }

  if (episodeTitle && seasonNumber) {
    return `${i18n("kausi_jakso_lyhyt", {
      language,
      s: { episode: episodeNumber, season: seasonNumber }
    })}: ${episodeTitle}`;
  }

  if (seasonNumber) {
    return i18n("kausi_jakso_pitka", {
      language,
      s: { episode: episodeNumber, season: seasonNumber }
    });
  }

  if (episodeTitle) {
    return `${i18n("jakso", { language })} ${episodeNumber}: ${episodeTitle}`;
  }

  return `${i18n("jakso", { language })} ${episodeNumber}`;
}

export const VideoTitle: FC = () => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const externalEvents = useStoreState.externalEvents();
  const uiSize = useStoreState.uiSize();

  const { backButton, language } = useEmbedParametersContext();
  const {
    episodeNumber,
    season: { seasonNumber } = {},
    series,
    title
  } = useMediaContext();

  const showTitle = series?.title || title;

  return (
    <TitleNavigation backButton={backButton}>
      {backButton && (
        <BackButton
          aria-label={i18n("takaisin", { language })}
          uiSize={uiSize}
          onClick={() => externalEvents?.dispatch("backButtonClicked")}>
          <ChevronLeft />
        </BackButton>
      )}
      {showTitle && (
        <Titles>
          <PrimaryTitle
            isMobile={device.isMobile}
            tabIndex={-1}
            uiSize={uiSize}>
            {localizedText(series?.title || title, language)}
          </PrimaryTitle>
          {series?.title && (
            <SecondaryTitle isMobile={device.isMobile} uiSize={uiSize}>
              {getSecondaryTitle({
                episodeNumber,
                episodeTitle: localizedText(title, language),
                language,
                seasonNumber
              })}
            </SecondaryTitle>
          )}
        </Titles>
      )}
    </TitleNavigation>
  );
};
